import { Edit } from '@carbon/icons-react'
import { Box, Button, Card, Flex, Spinner } from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type Edge,
  type EdgeChange,
  type EdgeTypes,
  type Node,
  type NodeChange,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import ButtonEdge from '@/components/playground/ButtonEdge'
import DynamicNode from '@/components/playground/DynamicNode'

// Playground components
import GenericNode from '@/components/playground/GenericNode'
import GroupNode from '@/components/playground/GroupNode'
import ArmNode from '~/components/nodes/armNode'
import ArmSimpleNode from '~/components/nodes/armSimpleNode'
import BioReactorPharmaNode from '~/components/nodes/BioReactorPharmaNode'
import BatteriesNode from '~/components/nodes/batteriesNode'
import BoilerNode from '~/components/nodes/boilerNode'
import ConfluentNode from '~/components/nodes/ConfluentNode'
import CarComputerNode from '~/components/nodes/carComputerNode'
import CloudNode from '~/components/nodes/cloudNode'
import CloudNodeBi from '~/components/nodes/cloudNodeBi'
import CollisionNode from '~/components/nodes/collisionNode'
import ControlSystemNode from '~/components/nodes/controlSystemNode'
import ConveyorNode from '~/components/nodes/conveyorNode'
import DatalakeNode from '~/components/nodes/datalakeNode'
// IT/Integration nodes
import DittoNode from '~/components/nodes/dittoNode'
import DittoNodeBi from '~/components/nodes/dittoNodeBi'
import DrillingNode from '~/components/nodes/drillingNode'
import DriverPadNode from '~/components/nodes/driverPadNode'
import EdgeBoxNode from '~/components/nodes/edgeBoxNode'
// HiveMQ nodes
import EdgeNode from '~/components/nodes/edgeNode'
import EdgeNodeBi from '~/components/nodes/edgeNodeBi'
import EntBrokerNode from '~/components/nodes/entBrokerNode'
import EntBrokerNodeBi from '~/components/nodes/entBrokerNodeBi'
import EnvSensorNode from '~/components/nodes/envSensorNode'
import ErpNode from '~/components/nodes/erpNode'
import EvChargerNode from '~/components/nodes/evChargerNode'
// Pharma nodes
import FeederNode from '~/components/nodes/feederNode'
import HeaterNode from '~/components/nodes/heaterNode'
// Smart Building nodes
import HeatPumpNode from '~/components/nodes/heatpumpNode'
import HistorianNode from '~/components/nodes/historianNode'
import HmiBottomNode from '~/components/nodes/hmiBottomNode'
import HopperNode from '~/components/nodes/hopperNode'
import MachineLearningNode from '~/components/nodes/machineLearningNode'
import MachineLearningTopNode from '~/components/nodes/machineLearningTopNode'
import MesBottomNode from '~/components/nodes/mesBottomNode'
import MicroServiceNode from '~/components/nodes/microServiceNode'
import MobileNode from '~/components/nodes/mobileNode'
import MongoDBNode from '~/components/nodes/mongoDBNode'
import MotorNode from '~/components/nodes/motorNode'
import OcppGatewayNode from '~/components/nodes/ocppGatewayNode'
import OilWellNode from '~/components/nodes/oilWellNode'
import PaintNode from '~/components/nodes/paintNode'
// Oil & Gas nodes
import PipelineNode from '~/components/nodes/pipelineNode'
// Factory/Manufacturing nodes
import PLCNode from '~/components/nodes/plcNode'
import PLCNodePhoenixContact from '~/components/nodes/plcNodePhoenixContact'
import PLCNodeRockwell from '~/components/nodes/plcNodeRockwell'
import PLCNodeSiemens from '~/components/nodes/plcNodeSiemens'
import PLCNodeWago from '~/components/nodes/plcNodeWago'
import PostgresNode from '~/components/nodes/postgresNode'
import PostgresNodeML from '~/components/nodes/postgresNodeML'
import PostgresTopNode from '~/components/nodes/postgresTopNode'
import PowerMeterNode from '~/components/nodes/powerMeterNode copy'
import PowerRegulatorNode from '~/components/nodes/powerRegulatorNode'
import RefineryNode from '~/components/nodes/refineryNode'
import ReportingNode from '~/components/nodes/reportingNode'
import RetailFuelNode from '~/components/nodes/retailFuelNode'
import StorageNode from '~/components/nodes/StorageNode'
import StoragePharmaNode from '~/components/nodes/StoragePharmaNode'
import ScadaTopNode from '~/components/nodes/scadaTopNode'
import ShippingNode from '~/components/nodes/shippingNode'
import SolarPanelNode from '~/components/nodes/solarPanelNode'
import StampingNode from '~/components/nodes/stampingNode'
import StreamingNode from '~/components/nodes/streamingNode'
import TankNode from '~/components/nodes/tankNode'
// Automotive/Logistics nodes
import TiresNode from '~/components/nodes/tiresNode'
import VentilationNode from '~/components/nodes/ventilationNode'
import WarehouseNode from '~/components/nodes/warehouseNode'
import WebNode from '~/components/nodes/webNode'
// Energy nodes
import WindTurbineNode from '~/components/nodes/windTubineNode'
import { PageHeader } from '~/components/shell'
import { flowTemplatesApi } from '~/lib/api'

export const Route = createFileRoute('/_app/pharma/flow')({ component: PharmaFlow })

const nodeTypes = {
  // Playground native nodes
  genericNode: GenericNode,
  dynamicNode: DynamicNode,
  groupNode: GroupNode,
  // HiveMQ nodes
  edgeNode: EdgeNode,
  edgeNodeBi: EdgeNodeBi,
  cloudNode: CloudNode,
  cloudNodeBi: CloudNodeBi,
  entBrokerNode: EntBrokerNode,
  entBrokerNodeBi: EntBrokerNodeBi,
  // IT/Integration nodes
  dittoNode: DittoNode,
  dittoNodeBi: DittoNodeBi,
  mobileNode: MobileNode,
  webNode: WebNode,
  machineLearningNode: MachineLearningNode,
  machineLearningTopNode: MachineLearningTopNode,
  microServiceNode: MicroServiceNode,
  postgresNode: PostgresNode,
  postgresNodeML: PostgresNodeML,
  postgresTopNode: PostgresTopNode,
  datalakeNode: DatalakeNode,
  streamingNode: StreamingNode,
  confluentNode: ConfluentNode,
  mongoDBNode: MongoDBNode,
  reportingNode: ReportingNode,
  historianNode: HistorianNode,
  erpNode: ErpNode,
  scadaTopNode: ScadaTopNode,
  hmiBottomNode: HmiBottomNode,
  mesBottomNode: MesBottomNode,
  // Factory/Manufacturing nodes
  plcNode: PLCNode,
  plcNodeSiemens: PLCNodeSiemens,
  plcNodeRockwell: PLCNodeRockwell,
  plcNodeWago: PLCNodeWago,
  plcNodePhoenixContact: PLCNodePhoenixContact,
  armNode: ArmNode,
  armSimpleNode: ArmSimpleNode,
  drillingNode: DrillingNode,
  paintNode: PaintNode,
  stampingNode: StampingNode,
  // Automotive/Logistics nodes
  tiresNode: TiresNode,
  collisionNode: CollisionNode,
  motorNode: MotorNode,
  tankNode: TankNode,
  carComputerNode: CarComputerNode,
  driverPadNode: DriverPadNode,
  warehouseNode: WarehouseNode,
  storageNode: StorageNode,
  conveyorsNode: ConveyorNode,
  // Energy nodes
  windTurbineNode: WindTurbineNode,
  solarPanelNode: SolarPanelNode,
  evChargerNode: EvChargerNode,
  batteriesNode: BatteriesNode,
  controlSystemNode: ControlSystemNode,
  powerMeterNode: PowerMeterNode,
  powerRegulatorNode: PowerRegulatorNode,
  ocppGatewayNode: OcppGatewayNode,
  // Oil & Gas nodes
  pipelineNode: PipelineNode,
  refineryNode: RefineryNode,
  oilwellNode: OilWellNode,
  retailfuelNode: RetailFuelNode,
  // Pharma nodes
  feederNode: FeederNode,
  hopperNode: HopperNode,
  storagePharmaNode: StoragePharmaNode,
  bioReactorPharmaNode: BioReactorPharmaNode,
  shippingNode: ShippingNode,
  // Smart Building nodes
  heatPumpNode: HeatPumpNode,
  heaterNode: HeaterNode,
  boilerNode: BoilerNode,
  ventilationNode: VentilationNode,
  envSensorNode: EnvSensorNode,
  edgeBoxNode: EdgeBoxNode,
}

const edgeTypes = {
  button: ButtonEdge,
}

// Helper to ensure group nodes have correct zIndex when loading
const normalizeNodes = (nodes: Node[]) => {
  return nodes.map((node) => {
    if (node.type === 'groupNode') {
      return { ...node, zIndex: -1 }
    }
    return node
  })
}

function PharmaFlow() {
  return (
    <ReactFlowProvider>
      <PharmaFlowContent />
    </ReactFlowProvider>
  )
}

function PharmaFlowContent() {
  const navigate = useNavigate()
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [isLocked, setIsLocked] = useState(false)
  const { fitView } = useReactFlow()

  const handleOpenInPlayground = () => {
    navigate({ to: '/playground', search: { template: 'pharma' } })
  }

  const { data: template, isLoading } = useQuery({
    queryKey: ['flow-template', 'pharma'],
    queryFn: () => flowTemplatesApi.getBySlug('pharma'),
  })

  useEffect(() => {
    if (template) {
      setNodes(normalizeNodes(template.nodes as Node[]))
      setEdges(template.edges as Edge[])
      setTimeout(() => {
        fitView({ padding: 0.1, duration: 200 })
      }, 100)
    }
  }, [template, fitView])

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  )
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  const onLockedChange = () => {
    setIsLocked(!isLocked)
  }

  if (isLoading) {
    return (
      <Flex h="100%" align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    )
  }

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title={template?.name || 'Pharma - Flow'}
        subtitle={
          template?.description ||
          'HiveMQ-powered pharmaceutical manufacturing data flow from sensors to cloud'
        }
        actions={
          <Button onClick={handleOpenInPlayground} colorPalette="yellow" size="sm">
            <Edit size={16} />
            Edit in Playground
          </Button>
        }
      />

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box h="100%" w="100%">
          <ReactFlow
            nodes={nodes}
            onNodesChange={onNodesChange}
            edges={edges}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes as EdgeTypes}
            fitView
            className="flow-customization"
            nodesDraggable={isLocked}
          >
            <Background className="flow-pharma-background" />
            <Controls onInteractiveChange={onLockedChange} />
          </ReactFlow>
        </Box>
      </Card.Root>
    </Flex>
  )
}
